/**
 * @fileoverview Job role select (owner / member / viewer)
 *
 * One control for picking a person's job-level role, with the same icon
 * language as the Organisation Roles explainer so a role reads the same way
 * wherever it appears. Used by the org settings team modal (where "Default"
 * is a valid choice, meaning the job creator decides) and by the create-job
 * team table (where a role is always set).
 */

import { Crown, Eye, User } from "lucide-react";
import type { JobRole } from "@cvx/jobs/schema";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from "@/ui/primitives";
import { cn } from "@/utils/misc";

/** Icon and label per job role. Mirrors the org-roles explainer icons. */
const JOB_ROLE_META: Record<JobRole, { Icon: typeof Crown; label: string }> = {
  owner: { Icon: Crown, label: "Owner" },
  member: { Icon: User, label: "Member" },
  viewer: { Icon: Eye, label: "Viewer" },
};

const JOB_ROLE_ORDER: JobRole[] = ["owner", "member", "viewer"];

/** Sentinel for "no explicit role" — only offered when `allowDefault`. */
const DEFAULT_VALUE = "__none__";

export function JobRoleSelect({
  value,
  onChange,
  allowDefault = false,
  defaultLabel = "Default",
  className,
}: {
  /** null means "Default" (only reachable when `allowDefault` is set). */
  value: JobRole | null;
  onChange: (role: JobRole | null) => void;
  allowDefault?: boolean;
  defaultLabel?: string;
  className?: string;
}) {
  const current = value ? JOB_ROLE_META[value] : null;

  return (
    <Select
      value={value ?? DEFAULT_VALUE}
      onValueChange={(v) =>
        onChange(v === DEFAULT_VALUE ? null : (v as JobRole))
      }
    >
      {/* The value is rendered here by hand so the icon shows in the trigger.
          Deliberately no <SelectValue />: it would print the label a second
          time alongside this one. */}
      <SelectTrigger className={cn("h-8 w-full text-xs", className)}>
        <span className="flex min-w-0 items-center gap-1.5">
          {current ? (
            <>
              <current.Icon className="size-3.5 shrink-0 text-primary/70" />
              <span className="truncate">{current.label}</span>
            </>
          ) : (
            <span className="truncate text-muted-foreground">
              {defaultLabel}
            </span>
          )}
        </span>
      </SelectTrigger>
      <SelectContent>
        {allowDefault && (
          <SelectItem value={DEFAULT_VALUE}>{defaultLabel}</SelectItem>
        )}
        {JOB_ROLE_ORDER.map((role) => {
          const { Icon, label } = JOB_ROLE_META[role];
          return (
            <SelectItem key={role} value={role}>
              <span className="flex items-center gap-1.5">
                <Icon className="size-3.5 shrink-0 text-primary/70" />
                {label}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
